import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaWhatsapp,
  FaShoppingCart,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaMapMarkerAlt,
  FaAward,
  FaInstagram,
} from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import { useCart } from "../context/CartContext";
import CartDrawer from "./CartDrawer";
import CanliArama from "./CanliArama";
import CalismaRozeti from "./CalismaRozeti";
import { VITRIN_URUNLERI, vitrinIcerik } from "../data/vitrinUrunleri";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../assets/akkuzeylogo.png";
import logoKirpik from "../assets/akkuzeylogo-kirpik.png"; // kenar boşlukları kırpılmış; navbar'da büyük görünsün diye

import { useTranslation } from "react-i18next";
import i18n from "../i18n/i18n";

const LANGS = [
  { code: "tr", label: "TR", flag: "/images/bayraklar/tr.webp" },
  { code: "en", label: "EN", flag: "/images/bayraklar/eng.webp" },
  { code: "ar", label: "AR", flag: "/images/bayraklar/ar.webp" },
  { code: "fr", label: "FR", flag: "/images/bayraklar/fr.webp" },
];

const TELEFON = "+90 537 798 11 29";
const EPOSTA = "akkuzey@hotmail.com";
const INSTAGRAM = "https://www.instagram.com/akkuzeyinsaat/";

// Siyah kategori çubuğundaki ana kategoriler (kısa adları navbar.cats altında)
const KATEGORI_CUBUGU = [
  { key: "insaat", slug: "insaat-malzemeleri" },
  { key: "hirdavat", slug: "hirdavat-urunleri" },
  { key: "elAletleri", slug: "el-aletleri" },
  { key: "boya", slug: "boya-firca-ve-rulo-grubu" },
  { key: "yapiKimyasallari", slug: "yapi-kimyasallari" },
  { key: "tesisat", slug: "mekanik-ve-tesisat-malzemeleri" },
  { key: "banyo", slug: "vitrifiye-ve-armaturler" },
  { key: "elektrik", slug: "elektrik-grubu-malzemeler" },
  { key: "makinalar", slug: "insaat-makinalari" },
  { key: "kesme", slug: "kesici-delici-ve-asindiricilar" },
];

// Sürekli sağdan sola akan iletişim şeridi. Üzerine gelince durur,
// "hareketi azalt" tercihinde hiç akmaz.
const KayanSerit = () => {
  const { t } = useTranslation();
  const dil = (i18n.language || "tr").split("-")[0];
  const ogeler = [
    // Öne çıkan yeni ürünler: şeridin en başında, tıklanınca tanıtım sayfası
    ...VITRIN_URUNLERI.map((v) => ({
      ozel: (
        <Link to={`/yeni/${v.slug}`} className="flex items-center gap-2 font-semibold text-white hover:underline">
          <span className="rounded-full bg-accent px-2 py-[1px] text-[10.5px] font-bold uppercase tracking-wide">{t("spotlight.newBadge")}</span>
          {vitrinIcerik(v, dil).serit}
        </Link>
      ),
    })),
    { ozel: <CalismaRozeti koyu /> },
    { ikon: FaAward, metin: t("home.hero.eyebrow") },
    { ikon: FaPhoneAlt, metin: TELEFON, href: `tel:${TELEFON.replace(/\s/g, "")}` },
    { ikon: FaEnvelope, metin: EPOSTA, href: `mailto:${EPOSTA}` },
    { ikon: FaInstagram, metin: "@akkuzeyinsaat", href: INSTAGRAM, disarida: true },
    { ikon: FaClock, metin: `${t("footer.contactInfo.weekdays")} · ${t("footer.contactInfo.sunday")}` },
    { ikon: FaMapMarkerAlt, metin: t("footer.contactInfo.address") },
  ];
  // Tek kopya geniş ekrandan kısa kalmasın diye öğeler iki kez; animasyon için bu blok da iki kez basılır.
  const blok = [...ogeler, ...ogeler];

  return (
    <div dir="ltr" className="group overflow-hidden bg-ink text-white">
      <div className="flex w-max animate-kayanSerit group-hover:[animation-play-state:paused] motion-reduce:animate-none">
        {[0, 1].map((kopya) => (
          <ul key={kopya} aria-hidden={kopya === 1} className="flex shrink-0 items-center">
            {blok.map((o, i) => {
              const Ikon = o.ikon;
              const icerik = Ikon && (
                <>
                  <Ikon className="shrink-0 text-accent" size={11} />
                  <span>{o.metin}</span>
                </>
              );
              return (
                <li key={i} className="flex items-center whitespace-nowrap text-[12.5px] tracking-wide text-white/85">
                  {o.ozel ? (
                    <span className="flex items-center gap-2 px-7 py-2">{o.ozel}</span>
                  ) : o.href ? (
                    <a
                      href={o.href}
                      tabIndex={kopya ? -1 : 0}
                      {...(o.disarida && { target: "_blank", rel: "noopener noreferrer" })}
                      className="flex items-center gap-2 px-7 py-2 transition-colors hover:text-white">
                      {icerik}
                    </a>
                  ) : (
                    <span className="flex items-center gap-2 px-7 py-2">{icerik}</span>
                  )}
                  <span aria-hidden="true" className="text-white/25">•</span>
                </li>
              );
            })}
          </ul>
        ))}
      </div>
    </div>
  );
};

// Siyah kategori çubuğu. Sığmayan ekranlarda (mobil, dar masaüstü) yana kayar;
// devamı olduğu kenardaki soluk geçiş ve ok düğmeleriyle belli olur.
// Not: içerik taşarken "justify-center" kullanılmaz — baştaki öğeleri keser; ortalama iç kapta "mx-auto w-max" ile yapılır.
const KategoriCubugu = ({ t, kategoriAktif, tumuAktif, yol }) => {
  const kapRef = useRef(null);
  const [sol, setSol] = useState(false);
  const [sag, setSag] = useState(false);

  const olc = () => {
    const k = kapRef.current;
    if (!k) return;
    // RTL'de scrollLeft negatif gelir; mutlak değerle çalışılır
    const x = Math.abs(k.scrollLeft);
    setSol(x > 4);
    setSag(x + k.clientWidth < k.scrollWidth - 4);
  };

  useEffect(() => {
    olc();
    window.addEventListener("resize", olc);
    return () => window.removeEventListener("resize", olc);
  }, []);

  // Aktif kategori görünür alana kaydırılır (mobilde sayfaya girince seçili kategori görünsün)
  useEffect(() => {
    // scrollIntoView sayfanın dikey kaydırmasıyla çakışıyor; yatay konum doğrudan hesaplanır
    const hizala = () => {
      const k = kapRef.current;
      const aktif = k?.querySelector('[aria-current="page"]');
      if (k && aktif) {
        const kr = k.getBoundingClientRect();
        const ar = aktif.getBoundingClientRect();
        k.scrollTo({ left: k.scrollLeft + (ar.left - kr.left) - (kr.width - ar.width) / 2, behavior: "instant" });
      } else if (k) {
        k.scrollTo({ left: 0, behavior: "instant" }); // aktif kategori yoksa baştan göster
      }
      olc();
    };
    hizala();
    const zamanlayici = setTimeout(hizala, 350); // yazı tipi yüklenince genişlikler değişebilir
    return () => clearTimeout(zamanlayici);
  }, [yol]);

  const kaydir = (yon) => {
    const k = kapRef.current;
    const rtl = document.documentElement.dir === "rtl";
    k?.scrollBy({ left: yon * (rtl ? -1 : 1) * k.clientWidth * 0.7, behavior: "smooth" });
  };

  const oge = (aktif, ekstra = "") =>
    `shrink-0 whitespace-nowrap border-b-2 px-3.5 py-3 text-[13.5px] transition-colors sm:text-[14px] xl:px-4 ${ekstra} ${
      aktif ? "border-accent font-semibold text-white" : "border-transparent font-medium text-white/80 hover:text-white"
    }`;

  const Ok = ({ yon, gorunur }) => (
    <button
      type="button"
      onClick={() => kaydir(yon)}
      aria-label={yon < 0 ? "‹" : "›"}
      tabIndex={-1}
      className={`absolute inset-y-0 z-10 flex w-10 items-center transition-opacity duration-200 ${
        yon < 0 ? "start-0 justify-start bg-gradient-to-r from-ink via-ink/90 to-transparent ps-1.5 rtl:bg-gradient-to-l" : "end-0 justify-end bg-gradient-to-l from-ink via-ink/90 to-transparent pe-1.5 rtl:bg-gradient-to-r"
      } ${gorunur ? "opacity-100" : "pointer-events-none opacity-0"}`}
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-[15px] leading-none text-white rtl:rotate-180">
        {yon < 0 ? "‹" : "›"}
      </span>
    </button>
  );

  return (
    <nav aria-label={t("products")} className="relative bg-ink">
      <Ok yon={-1} gorunur={sol} />
      <div ref={kapRef} onScroll={olc} className="scrollbar-hide overflow-x-auto">
        <div className="mx-auto flex w-max items-center px-2 sm:px-4">
          {KATEGORI_CUBUGU.map((k) => {
            const aktif = kategoriAktif(k.slug);
            return (
              <Link key={k.slug} to={`/urunler/${k.slug}`} aria-current={aktif ? "page" : undefined} className={oge(aktif)}>
                {t(`navbar.cats.${k.key}`)}
              </Link>
            );
          })}
          <Link to="/urunler" aria-current={tumuAktif ? "page" : undefined} className={oge(tumuAktif, "font-semibold text-white")}>
            {t("navbar.allCategories")}
          </Link>
        </div>
      </div>
      <Ok yon={1} gorunur={sag} />
    </nav>
  );
};

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(null);
  const [sepetAcik, setSepetAcik] = useState(false);
  const { toplamAdet } = useCart();

  const { t } = useTranslation();
  const location = useLocation();
  const menuRef = useRef(null);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  };

  useEffect(() => {
    // Adreste #bölüm varsa kaydırmayı ScrollToTop yapar (o bölüme); burada en üste çekilmez
    if (location.hash) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, location.hash]);

  // Mobil menü açıkken arka planı kilitle
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const mainMenu = [
    { name: t("homeNavbar"), path: "/" },
    { name: t("products"), path: "/urunler" },
    { name: t("calc.navbarItem"), path: "/hesaplayicilar" },
    { name: t("guide.menu"), path: "/rehber" },
    { name: t("corporate"), path: "/kurumsal", dropdown: "corporate" },
    { name: t("affiliates"), path: "/istiraklerimiz", dropdown: "affiliates" },
    { name: t("dealerships"), path: "/bayilikler" },
    { name: t("contactNavbar"), path: "/iletisim" },
  ];

  const subMenus = {
    corporate: [
      { name: t("subMenus.corporate.about"), path: "/about" },
      { name: t("subMenus.corporate.contact"), path: "/iletisim" },
      {
        name: t("subMenus.corporate.qualityPolicy"),
        path: "/kurumsal/kalite-politikamiz",
      },
      {
        name: t("dealerships"),
        path: "/bayilikler",
      },
      { name: t("subMenus.corporate.faq"), path: "/sss" },
    ],
    affiliates: [
      { name: t("subMenus.affiliates.construction"), path: "" },
      { name: t("subMenus.affiliates.automotive"), path: "" },
    ],
  };

  const getSubMenu = (key) => subMenus[key] || [];

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const kategoriAktif = (slug) => location.pathname === `/urunler/${slug}`;

  return (
    <>
      <KayanSerit />

      {/* İNCE LİNK ÇUBUĞU (masaüstü) */}
      <div className="hidden border-b border-line bg-white lg:block">
        <div className="container-page flex h-10 items-center justify-between">
          <nav className="flex items-center text-[13px]">
            {mainMenu.map((item, idx) => (
              <div
                key={idx}
                className="relative flex items-center"
                onMouseEnter={() => item.dropdown && setDropdownKey(item.dropdown)}
                onMouseLeave={() => item.dropdown && setDropdownKey(null)}
              >
                {idx > 0 && <span aria-hidden="true" className="h-3.5 w-px bg-ink/25" />}
                {item.dropdown ? (
                  <span className={`cursor-pointer px-3.5 py-2 transition-colors ${dropdownKey === item.dropdown ? "text-ink" : "text-body hover:text-ink"}`}>
                    {item.name}
                  </span>
                ) : (
                  <Link
                    to={item.path}
                    className={`px-3.5 py-2 transition-colors ${isActive(item.path) ? "font-medium text-ink" : "text-body hover:text-ink"}`}
                  >
                    {item.name}
                  </Link>
                )}

                <AnimatePresence>
                  {item.dropdown && dropdownKey === item.dropdown && (
                    <motion.ul
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-2 top-full z-[60] w-64 overflow-hidden rounded-lg border border-line bg-white py-1.5 shadow-pop"
                    >
                      {getSubMenu(item.dropdown).map((sub, i) => (
                        <li key={i}>
                          {/* Sayfası olmayan başlıklar link gibi davranmaz */}
                          {sub.path ? (
                            <Link to={sub.path} className="block px-4 py-2 text-[14px] text-body transition-colors hover:bg-canvas hover:text-ink">
                              {sub.name}
                            </Link>
                          ) : (
                            <span className="block cursor-default px-4 py-2 text-[14px] text-muted">{sub.name}</span>
                          )}
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <CalismaRozeti kisa />
            <Link to="/stores" className="flex items-center gap-1.5 text-[13px] text-body transition-colors hover:text-ink">
              <MdLocationOn size={15} className="text-accent" />
              {t("storesFooter")}
            </Link>
            <div className="flex items-center gap-1.5 border-l border-line pl-4">
              {LANGS.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  aria-label={lang.label}
                  className={`overflow-hidden rounded-[3px] transition-opacity duration-200 ${
                    i18n.language === lang.code ? "opacity-100 ring-1 ring-ink/30" : "opacity-45 hover:opacity-100"
                  }`}
                >
                  <img src={lang.flag} alt={lang.label} className="block h-4 w-[22px] object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-white shadow-[0_1px_0_rgba(16,19,23,0.06)]">
        {/* ANA SATIR: büyük logo · arama · aksiyonlar */}
        <div className="container-page flex items-center gap-5 py-3 lg:gap-10 lg:py-4">
          <Link to="/" className="flex shrink-0 items-center" aria-label={t("ui.logoAlt")}>
            <img src={logoKirpik} alt={t("ui.logoAlt")} className="h-14 w-auto sm:h-16 lg:h-[88px]" />
          </Link>

          <CanliArama ekSinif="hidden flex-1 lg:block" />

          <div className="ml-auto flex shrink-0 items-center gap-2.5 lg:ml-0">
            <a
              href={`tel:${TELEFON.replace(/\s/g, "")}`}
              className="hidden items-center gap-3 rounded-md border border-line px-4 py-2.5 transition-colors hover:border-ink/40 xl:flex"
            >
              <FaPhoneAlt size={16} className="text-accent" />
              <span className="leading-tight">
                <span className="block text-[11px] text-muted">{t("navbar.callUs")}</span>
                <span dir="ltr" className="block text-[13px] font-semibold text-ink">{TELEFON}</span>
              </span>
            </a>

            <button
              onClick={() => setSepetAcik(true)}
              aria-label={t("navbar.myCart")}
              className="relative inline-flex items-center gap-2.5 rounded-md border border-line px-3.5 py-2.5 text-[13px] font-medium text-ink transition-colors hover:border-ink/40 sm:px-4 sm:py-3"
            >
              <FaShoppingCart size={17} />
              <span className="hidden sm:inline">
                {t("navbar.myCart")} <span className="tabular-nums text-muted">({toplamAdet})</span>
              </span>
              {toplamAdet > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1 text-[11px] font-semibold tabular-nums text-white sm:hidden">
                  {toplamAdet}
                </span>
              )}
            </button>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? t("ui.closeMenu") : t("ui.openMenu")}
              aria-expanded={menuOpen}
              className="flex h-11 w-11 items-center justify-center rounded-md border border-line text-lg text-ink transition-colors hover:bg-canvas lg:hidden"
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobilde arama logonun altında tam genişlik */}
        <div className="container-page pb-3 lg:hidden"><CanliArama ekSinif="block" onSec={() => setMenuOpen(false)} /></div>

        {/* SİYAH KATEGORİ ÇUBUĞU */}
        <KategoriCubugu t={t} kategoriAktif={kategoriAktif} tumuAktif={location.pathname === "/urunler"} yol={location.pathname} />
      </header>

      {/* MOBİL FULLSCREEN MENÜ */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            ref={menuRef}
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 overflow-y-auto bg-white lg:hidden"
          >
            <div className="flex h-[84px] items-center justify-between border-b border-line px-5">
              <img src={logo} alt={t("ui.logoAlt")} className="h-12 w-auto" />
              <button
                onClick={() => setMenuOpen(false)}
                aria-label={t("ui.closeMenu")}
                className="flex h-9 w-9 items-center justify-center rounded-md text-xl text-ink transition-colors duration-200 hover:bg-canvas"
              >
                <FaTimes />
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="divide-y divide-line">
                {mainMenu.map((item, idx) =>
                  item.dropdown ? (
                    <div key={idx} className="py-1">
                      <button
                        onClick={() =>
                          setDropdownKey(
                            dropdownKey === item.dropdown ? null : item.dropdown
                          )
                        }
                        className="flex w-full items-center justify-between py-3 text-left text-[15px] font-medium text-ink"
                      >
                        {item.name}
                        <span
                          className={`text-muted transition-transform duration-200 ${
                            dropdownKey === item.dropdown ? "rotate-45" : ""
                          }`}
                        >
                          +
                        </span>
                      </button>

                      {dropdownKey === item.dropdown && (
                        <div className="mb-2 space-y-1 border-l border-line pl-4">
                          {getSubMenu(item.dropdown).map((sub, i) =>
                            sub.path ? (
                              <Link
                                key={i}
                                to={sub.path}
                                className="block py-2 text-[14px] text-body"
                                onClick={() => setMenuOpen(false)}
                              >
                                {sub.name}
                              </Link>
                            ) : (
                              <span
                                key={i}
                                className="block py-2 text-[14px] text-muted"
                              >
                                {sub.name}
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={idx}
                      to={item.path}
                      className="block py-3.5 text-[15px] font-medium text-ink"
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )
                )}
              </div>

              {/* Mobil Dil Seçimi */}
              <div className="mt-8 border-t border-line pt-6">
                <p className="eyebrow mb-3">{t("chooseLanguage")}</p>

                <div className="flex gap-2">
                  {LANGS.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-[13px] font-medium transition-colors duration-200 ${
                        i18n.language === lang.code
                          ? "border-ink/30 bg-canvas text-ink"
                          : "border-line text-body"
                      }`}
                    >
                      <img
                        src={lang.flag}
                        alt={lang.label}
                        className="h-4 w-[22px] rounded-[2px] object-cover"
                      />
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp */}
      <a
        href="https://wa.me/905377981129?text=Merhaba%20bilgi%20almak%20istiyorum"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("ui.whatsapp")}
        className={`fixed bottom-6 right-6 z-[90] h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-pop transition-colors duration-200 hover:bg-[#1FB955] ${
          // Tanıtım sayfalarında telefonda alttaki sabit WhatsApp çubuğu var; üst üste binmesin
          location.pathname.startsWith("/yeni/") ? "hidden sm:flex" : "flex"
        }`}
      >
        <FaWhatsapp size={24} />
      </a>

      {/* TEKLİF SEPETİ */}
      <CartDrawer acik={sepetAcik} kapat={() => setSepetAcik(false)} />
    </>
  );
};

export default Navbar;
